/**
 * USD per month for one provider over the overview's window (A7Y-5559), in
 * the provider's colour, with grid and axes themed as
 * `audit-spend/DailyTrendChart.tsx` does. Perplexity's Ledger top-ups ride
 * beside its call-log spend as a hatched second series: what was paid in
 * credit, against what our own calls consumed.
 */
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AiApisOverviewResponse } from '@a7y/types';
import { useTheme } from '@/contexts/useTheme';
import { formatInt, formatUsd2 } from '@/pages/audit-spend/format';
import { formatMonthLong, formatMonthShort } from './meta';

type ProviderOverview = AiApisOverviewResponse['providers'][number];

interface ChartRow {
  month: string;
  costUsd: number;
  topUpUsd?: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

function SpendTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartRow }>;
}) {
  const row = active ? payload?.[0]?.payload : undefined;
  if (!row) return null;
  return (
    <div className="rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-xs shadow-sm">
      <div className="mb-1 font-medium text-zinc-900 dark:text-zinc-100">
        {formatMonthLong(row.month)}
      </div>
      <div className="text-zinc-700 dark:text-zinc-300">{formatUsd2(row.costUsd)}</div>
      {row.topUpUsd !== undefined ? (
        <div className="text-zinc-700 dark:text-zinc-300">
          Ledger top-ups {formatUsd2(row.topUpUsd)}
        </div>
      ) : null}
      <div className="mt-1 text-zinc-500 dark:text-zinc-400">
        {formatInt(row.inputTokens)} in · {formatInt(row.outputTokens)} out ·{' '}
        {formatInt(row.cacheReadTokens + row.cacheWriteTokens)} cache
      </div>
    </div>
  );
}

export function MonthlySpendChart({
  provider,
  colorHex,
}: {
  provider: ProviderOverview;
  colorHex: string;
}) {
  const { resolvedTheme } = useTheme();
  const axisTick = { fontSize: 11, fill: '#9ca3af' };
  const gridStroke = resolvedTheme === 'dark' ? '#3f3f46' : '#e4e4e7';
  const topUps = provider.ledgerTopUpsUsdByMonth;
  const hatchId = `ai-apis-hatch-${provider.provider}`;

  const rows: ChartRow[] = provider.monthly.map((m) => ({
    month: m.month,
    costUsd: m.costUsd,
    ...(topUps ? { topUpUsd: topUps[m.month] ?? 0 } : {}),
    inputTokens: m.inputTokens,
    outputTokens: m.outputTokens,
    cacheReadTokens: m.cacheReadTokens,
    cacheWriteTokens: m.cacheWriteTokens,
  }));

  return (
    <div className="h-36" data-testid={`monthly-chart-${provider.provider}`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          {topUps ? (
            <defs>
              <pattern
                id={hatchId}
                width="6"
                height="6"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect width="6" height="6" fill={colorHex} fillOpacity={0.15} />
                <line x1="0" y1="0" x2="0" y2="6" stroke={colorHex} strokeWidth="3" />
              </pattern>
            </defs>
          ) : null}
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
          <XAxis
            dataKey="month"
            tickFormatter={formatMonthShort}
            tick={axisTick}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={axisTick}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(v: number) =>
              `$${v >= 1000 ? `${Math.round(v / 100) / 10}k` : Math.round(v)}`
            }
          />
          <Tooltip content={<SpendTooltip />} cursor={{ fill: gridStroke, fillOpacity: 0.4 }} />
          <Bar
            dataKey="costUsd"
            name="Spend"
            fill={colorHex}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
          {topUps ? (
            <Bar
              dataKey="topUpUsd"
              name="Ledger top-ups"
              fill={`url(#${hatchId})`}
              stroke={colorHex}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          ) : null}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
